import { createSelector } from 'reselect'

import { WalletType } from 'common/billing/wallet/interfaces/walletType'
import { DEFAULT_PAYMENT_AMOUNT } from 'common/billing/wallet/walletConstants'
import { ChannelStatus } from 'common/core/interfaces/channelStatuses'
import { getCurrentAccount, getIsAccountFlagEnabled } from 'common/core/selectors/appSelectors'
import {
  getWhatsAppChannel,
  isWhatsAppChannelConnected,
} from 'common/core/selectors/channelSelectors'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

import {
  WalletRefillOptions,
  WalletState,
  WalletTransaction,
  WalletUsageSummary,
} from './walletInterfaces'

export const getWalletState = (state: RootState): WalletState => state.billing.wallet

export const hasWallet = (state: RootState): boolean => state.billing.wallet.wallet.hasWallet

export const getAutoRefillStatus = (state: RootState): boolean =>
  getWalletState(state).wallet.autoRefill.isEnabled

export const getUsageSummary = (state: RootState): WalletUsageSummary[] =>
  state.billing.wallet.usageHistory.summary

export const getBalance = (state: RootState): number => getWalletState(state).wallet.balance

export const getIsCreditWallet = (state: RootState): boolean =>
  getWalletState(state).wallet.walletType === WalletType.CREDIT

export const getUsageTransactions = (state: RootState): WalletTransaction[] =>
  state.billing.wallet.usageHistory.transactions

export const getHasOlderTransactions = (state: RootState): boolean =>
  state.billing.wallet.usageHistory.hasOlderTransactions

/**
 * Selector to get state of active non-credit wallet with any balance.
 */
export const getIsNotCreditWalletActive = createSelector(
  [hasWallet, getBalance, getIsCreditWallet],
  (hasWallet, balance, isCredit) => {
    return hasWallet && balance > 0 && !isCredit
  },
)

export const getDefaultAmount = (state: RootState): number => {
  const options = state.billing.wallet.wallet.options

  if (options && options.amountOptions && options.amountOptions.length > 0) {
    return options.amountOptions[0].amount
  }

  return DEFAULT_PAYMENT_AMOUNT
}

export const getWalletRefillOptions = (state: RootState): WalletRefillOptions => {
  const options = state.billing.wallet.wallet.options

  return {
    triggerAutorefillAmountOptions: options?.triggerAutorefillAmountOptions ?? [],
    dailyLimitOptions: options?.dailyLimitOptions ?? [],
    amountOptions: options?.amountOptions ?? [],
  }
}

/**
 * Selector to get the state of showing the wallet ONLY if there are active wallet, email channel, or WhatsApp channel enabled
 */
export const getIsShowWalletData = createSelector(
  [getCurrentAccount, getIsNotCreditWalletActive, getWhatsAppChannel],
  (account, isWalletActive, whatsAppChannel) => {
    const isEmailChannelActive = account.email_channel?.status === ChannelStatus.ACTIVE

    let isWhatsAppChannelActive = false
    if (isWhatsAppChannelConnected(whatsAppChannel)) {
      isWhatsAppChannelActive = whatsAppChannel.status === ChannelStatus.ACTIVE
    }

    return isWalletActive || isEmailChannelActive || isWhatsAppChannelActive
  },
)

/**
 * This selector determines whether the "Request Wallet Refund" step should be displayed to the user.
 *
 * It evaluates the following conditions:
 * 1. The account flag `ENABLE_NON_EMPTY_WALLET_AS_BLOCKER_FOR_DELETION` must be enabled.
 * 2. The user must have an active wallet.
 * 3. The wallet balance must be greater than zero.
 *
 * If all conditions are met, the selector returns `true`, indicating that the refund step should be shown.
 * Otherwise, it returns `false`.
 */
export const getIsShowRequestWalletRefundStep = createSelector(
  [
    (state) =>
      getIsAccountFlagEnabled(state, AccountFlag.ENABLE_NON_EMPTY_WALLET_AS_BLOCKER_FOR_DELETION),
    hasWallet,
    getBalance,
  ],
  (isFlagEnabled, walletExists, balance) => isFlagEnabled && walletExists && balance > 0,
)
